import { useCallback, useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { isPreviewId } from '@actual-app/core/shared/transactions';
import type { TransactionEntity } from '@actual-app/core/types/models';

import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import { usePagosFijos } from '#components/mobile/budget/usePagosFijos';
import { useNavigate } from '#hooks/useNavigate';

import {
  leerIdPrevisto,
  mesSiguiente,
  previstosDePagosFijos,
} from './previstosPagosFijos';

/**
 * Filas previstas de «Próximos» desde los pagos fijos (este mes sin pagar,
 * los de final del mes anterior aún en su margen y el siguiente dentro de
 * 7 días). `cuenta` limita a las cuentas que
 * muestra la lista.
 */
export function usePrevistosPagosFijos({
  cuenta,
}: { cuenta?: (cuentaId: string) => boolean } = {}): {
  previewTransactions: TransactionEntity[];
  isLoading: boolean;
} {
  const hoy = monthUtils.currentDay();
  const actual = usePagosFijos(hoy.slice(0, 7));
  const anterior = usePagosFijos(monthUtils.subMonths(hoy.slice(0, 7), 1));
  const siguiente = usePagosFijos(mesSiguiente(hoy));
  const previewTransactions = useMemo(
    () =>
      previstosDePagosFijos({
        actuales: actual.pagos,
        anteriores: anterior.pagos,
        siguientes: siguiente.pagos,
        hoy,
        cuenta,
      }),
    [actual.pagos, anterior.pagos, siguiente.pagos, hoy, cuenta],
  );
  return {
    previewTransactions,
    isLoading: actual.cargando || anterior.cargando || siguiente.cargando,
  };
}

/**
 * Tocar una fila: los movimientos se abren; un previsto de pago fijo abre
 * la ficha de su categoría (son domiciliados: el cargo llega del banco, no
 * se registra a mano). Los demás previstos, nada.
 */
export function useAbrirMovimiento() {
  const navigate = useNavigate();
  return useCallback(
    (transaction: TransactionEntity) => {
      if (!isPreviewId(transaction.id)) {
        void navigate(`/transactions/${transaction.id}`);
        return;
      }
      const previsto = leerIdPrevisto(transaction.id);
      if (!previsto) {
        return;
      }
      void navigate(
        rutaFicha(previsto.categoriaId, previsto.fecha.slice(0, 7)),
      );
    },
    [navigate],
  );
}
